import { useLabs } from "../grid/etLabs";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { Checkbox, IconButton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { createPortal } from "react-dom";
import parse from "html-react-parser";
import { observer } from "mobx-react-lite";
import React, { Fragment, useRef, useEffect, useState } from "react";
import logoSite from "../../assets/brand/logo_site.svg";
import Clock from "../clock/clock";
import { maxLines } from "../constants";
import { getResponsiveValue } from "../responsive/mediaQueries";
import { useEtBreakpoints, useEtExtras } from "../responsive/useEtView";
import { getEmbedParentOrigin } from "../../utils/embedBridge";
import "./itemContent.css";

// Convert to proper React component with uppercase first letter
// Inside the Premiere panel, tapping row TEXT must NOT select (it made
// scrolling impossible) — there, text taps play-from-word like desktop and
// selection is checkbox / double-click only. Constant for the page's life.
const IS_PANEL_EMBED = typeof window !== "undefined" && getEmbedParentOrigin() === "file://";

// Download and Share under a phone thumbnail are the SAME object in two colours: identical
// width, height, radius and type, stacked. Two differently-sized pills read as a mistake.
// The desktop pair under the timecodes: two identical circles, one neutral, one cherry.
// They were a bare 15px glyph and a bare SVG before, on different baselines.
const ROW_ICON = {
    padding: 0, width: 24, height: 24, borderRadius: '50%',
    border: '1px solid', boxSizing: 'border-box', flex: 'none',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const XS_BTN = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    cursor: 'pointer', color: '#fff', width: '132px', height: '30px', padding: 0,
    borderRadius: '8px', fontSize: '12px', fontWeight: 700, fontFamily: 'Outfit, sans-serif',
    boxSizing: 'border-box',
};

export const ItemContent = observer(({ item, data, header, filterStore, videoFile, onVideoClick, onWordClick, onWordRightClick, onUnsplit, onCheckboxChange, currentView, userStore, precision, onJumpToItem, showJump = false, onAdjustTimes, showExtraMobileColumns = false, onSingleClipDownload, onSingleClipShare = null, penMode = false, splitCursor = null, transcriptStyle = null, translations = null, editedSentences = null, onEditSentence = null, speakerNames = null, onRenameSpeaker = null, clipThumbnails = null, thumbnailsGenerating = false, speakerHeadings = false, prevSpeaker = null, onWordSearch = null, onSecondsClick = null, onLengthClick = null, onSpeakerClick = null, onBoolClick = null, onSentimentClick = null }) => {
    const theme = useTheme();
    const dragStartRef = useRef(null);
    // Inline speaker rename (v1): click the Spkr cell -> edit the name for that speaker letter.
    const [spkEditing, setSpkEditing] = useState(false);
    const [spkDraft, setSpkDraft] = useState('');

    // Pre-warm: seek an ALREADY-loaded clip to its start so play is snappy.
    // IMPORTANT: we must NOT force-decode here. Setting preload='auto' on every visible row
    // made many videos decode at once — browsers cap concurrent decodes (~4-6), so the
    // thumbnails (posters) went BLANK. The poster is the real thumbnail JPEG and shows fine
    // on its own with preload='none'; we only "top up" videos the browser already loaded,
    // and never kick off new decodes. Play-on-click still loads on demand.
    const clipStart = data ? data['start_in'] : null;
    useEffect(() => {
        if (clipStart == null) return;
        if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
        const v = document.getElementById(`v-${data['id']}`);
        if (!v) return;
        // Only seek if the video already has frame data (readyState >= 2). Do NOT set
        // preload='auto' and do NOT wait on loadedmetadata — that's what caused the decode
        // storm that blanked every thumbnail.
        if (v.readyState >= 2 && v.paused) {
            try {
                if (Math.abs((v.currentTime || 0) - clipStart) > 0.3) v.currentTime = clipStart;
            } catch (_) { /* ignore */ }
        }
    }, [item, videoFile, clipStart]);

    // Use media query hooks to detect screen size using our constants
    const { isXSmallScreen, isSmallScreen, isMediumScreen, isLargeScreen } = useEtBreakpoints();
    const labs = useLabs();
    const etExtras = useEtExtras(); // toggleable mobile thumbnail extras (download / timecode / big chip)
    // Portrait phone → cap the transcript so it reads in chunks (the "phone" look); rotate to
    // landscape → let it run wide. This is what makes Phone mode look right on any width.
    const isLandscape = useMediaQuery('(orientation: landscape)');
    // Compact-view knobs: a bigger thumbnail (keeps the 142:80 ratio) and how much room the
    // transcript leaves left of it.
    const XS_THUMB_W = etExtras.bigThumb ? 208 : 142;
    const XS_THUMB_H = Math.round((XS_THUMB_W * 80) / 142);
    const XS_SENT_OFFSET = XS_THUMB_W + 38; // checkbox(26) + paddings

    // Must match header.jsx shouldShowColumn exactly
    const shouldShowColumn = (field) => {
        // Phone default: checkbox + fat thumbnail + full-width transcript, with the timecode
        // column rendered just past the right edge — swipe the text left and it rolls under
        // the pinned thumbnail as the ± trim steppers arrive (the "rotating column" effect).
        // Sec/Length/Q? stay behind the "show more" toggle.
        // If the timecode already shows under each thumbnail, drop the redundant timecode column.
        const xsBase = etExtras.timecode ? ['', 'play', 'sentence'] : ['', 'play', 'sentence', 'duration'];
        const xsExtra = ['sec', 'length', 'question'];
        const smVisible = ['', 'order', 'play', 'sentence', 'speaker', 'sentiment', 'sec', 'length', 'question', 'duration'];
        if (isXSmallScreen) return xsBase.includes(field) || (showExtraMobileColumns && xsExtra.includes(field));
        if (isSmallScreen)  return smVisible.includes(field) || showExtraMobileColumns;
        return true;
    };

    // Row-level selection for highlight
    const isRowSelected = filterStore.isSelectedSentence(data['id'], currentView);
    const selectionOrder = isRowSelected ? filterStore.getSelectionOrder(data['id'], currentView) : null;
    // Search-context neighbour rows show dimmed (but stay fully selectable). A selected row
    // always wins — if you grab a context clip, it lights up like any other selection.
    const isContextRow = !!data['_searchContext'];
    // FILLER fade: in Dim mode the AI-flagged throwaway clips drop back (low opacity) while
    // the keepers stay full-strength, so the good stuff visually pops. Selection always wins.
    const isFillerDim = !!data['_fillerDim'];
    const isFillerKeep = !!data['_fillerKeep'];
    const rowHighlight = isRowSelected
        ? { backgroundColor: 'rgba(229, 57, 53, 0.13)' }
        : (isContextRow ? { opacity: 0.5 } : (isFillerDim ? { opacity: 0.3 } : {}));

    // When a clip is pen-highlighted, the timecodes should reflect the HIGHLIGHT (the part
    // that actually exports) — including any +/- breathing room (padStart/padEnd) the user
    // nudged in — not the whole clip. We compute that display range here so the Clock shows
    // the true in/out of the trimmed phrase.
    const secondsToHMS = (totalSeconds) => {
        const ms = Math.round((totalSeconds % 1) * 1000);
        const s = Math.floor(totalSeconds) % 60;
        const m = Math.floor(totalSeconds / 60) % 60;
        const h = Math.floor(totalSeconds / 3600) % 24;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
    };
    const penHL = filterStore.getHighlight(data['id']);
    let displayDuration = data['duration'];
    let penAdjusted = false;
    if (penHL && data['utterance'] && data['utterance'].length) {
        const s = data['utterance'][penHL.start]?.[0];
        const e = data['utterance'][penHL.end]?.[1];
        if (s !== undefined && e !== undefined) {
            let sPad = Math.max(0, s - (penHL.padStart || 0));
            let ePad = Math.max(sPad + 0.05, e + (penHL.padEnd || 0));
            if (penHL.incAfter && data['end_in'] != null) ePad = Math.max(ePad, data['end_in']);
            if (penHL.incBefore && data['start_in'] != null) sPad = Math.min(sPad, Math.max(0, data['start_in']));
            displayDuration = [secondsToHMS(sPad), secondsToHMS(ePad)];
            penAdjusted = !!(penHL.padStart || penHL.padEnd || penHL.incAfter || penHL.incBefore);
        }
    }

    const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    // Sticky positioning for body cells (z-index 8 — below thead:10 and header cells:12)
    // XS pinning: checkbox(26px) + play(146px) sticky-left; the transcript is NOT sticky —
    // it scrolls behind the pinned thumbnails when you swipe left, and the timecode column
    // rolls in from past the right edge ("rotating" column effect).
    // Sticky cells must stay opaque (scrolled content passes under them), so when the row
    // is selected the red tint is layered as a gradient OVER the solid paper background —
    // a translucent backgroundColor here would let text show through the pinned cells.
    const getStickyStyle = (obj) => {
        if (!isXSmallScreen) return {};
        const bg = theme.palette.background.paper;
        const tint = isRowSelected
            ? { backgroundImage: 'linear-gradient(rgba(229, 57, 53, 0.13), rgba(229, 57, 53, 0.13))' }
            : {};
        if (obj.type === 'checkbox')  return { position: 'sticky', left: 0,      zIndex: 8, backgroundColor: bg, ...tint };
        if (obj.type === 'video')     return { position: 'sticky', left: '26px', zIndex: 8, backgroundColor: bg, ...tint };
        // duration is deliberately NOT sticky on xs: it lives past the right edge and
        // "arrives" when you swipe the transcript left — pinning it squished the text.
        return {};
    };


    // Responsive styling adjustments using our utility function
    const getResponsiveStyles = (obj) => {
        // Base styles that apply to all fields
        const baseStyles = {
            width: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: 'auto',
                    sm: 'auto',
                    md: 'auto',
                    default: obj.width
                },
                obj.width
            ),
            maxWidth: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: 'none',
                    sm: 'none',
                    md: 'none',
                    default: obj.width
                },
                obj.width
            ),
            padding: getResponsiveValue(
                isXSmallScreen,
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                {
                    xs: '2px',
                    sm: '2px',
                    md: '3px',
                    default: '4px'
                },
                '4px'
            ),
            textAlign: obj.align || 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        };

        // Special override for Clock component column. The timecode + the − / + steppers must
        // ALWAYS fit: when a nudge auto-reveals decimals (00:00:06 → 00:00:06.320) the text
        // widens, and the inherited maxWidth + overflow:hidden used to clip the + buttons off
        // the edge so they "disappeared" and the clip could no longer be adjusted. Size the
        // column to its content, keep it on one line, and never clip it.
        if (obj.type === 'icon' && obj.field === 'duration') {
            return {
                ...baseStyles,
                width: 'auto',
                minWidth: '150px',   // room for [−] 00:00:06.320 [+] on both the IN and OUT rows
                maxWidth: 'none',
                overflow: 'visible',
                whiteSpace: 'nowrap',
                padding: 0, // Remove padding from TD
                textAlign: 'center', // Center align TD content
                verticalAlign: 'middle', // Align vertically like header
            };
        }

        // Phone (xs): give the thumbnail a fixed, visible width so it doesn't collapse to
        // nothing — the #1 reason the ET was unusable in portrait. It's sticky, so it stays
        // pinned on the left as you scroll the rest of the row.
        // 146 = 142px video + 2px padding each side (itemContent.css pads .video 2px on xs);
        // the sticky offsets in getStickyStyle assume checkbox(26) + play(146).
        if (isXSmallScreen && obj.type === 'video') {
            const w = `${XS_THUMB_W + 4}px`;
            // vertical-align middle (not top): on a tall row (long transcript) a top-hugged thumbnail
            // + timecodes read as floating/wacky — centering the thumbnail block looks right.
            return { ...baseStyles, width: w, minWidth: w, maxWidth: w, verticalAlign: 'middle' };
        }

        // Phone (xs): pin the checkbox column to exactly 26px so the video's sticky
        // left offset (26px) always lines up — 'auto' let it drift with content.
        if (isXSmallScreen && obj.type === 'checkbox') {
            return { ...baseStyles, width: '26px', minWidth: '26px', maxWidth: '26px', padding: '2px 0', textAlign: 'center' };
        }

        // Desktop: the row-number + checkbox needs ~40px, but the column's tight width +
        // overflow:hidden clipped the box into a "[" at the left edge. Size to content and
        // never clip, so the (super important) checkbox is always fully visible.
        if (obj.type === 'checkbox') {
            return { ...baseStyles, width: 'auto', minWidth: '42px', maxWidth: 'none', overflow: 'visible', whiteSpace: 'nowrap' };
        }

        // Special case for sentence field - make it responsive to video width
        if (obj.field === 'sentence') {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            // Calculate sentence width based on video width
            // When video is smaller (30px), sentence gets more space
            // When video is larger (150px), sentence gets less space
            let calculatedSentenceWidth;

            if (isXSmallScreen) {
                // Phone: the transcript fills the width left of the pinned thumbnail, pushing the
                // timecode column just past the right edge (swipe left to roll it in). "narrowText"
                // caps the transcript so long lines wrap into readable chunks — and, being narrower,
                // the timecode column then shows alongside instead of only on scroll.
                calculatedSentenceWidth = isLandscape
                    ? `calc(100vw - ${XS_SENT_OFFSET}px)`                       // rotated → wide
                    : `min(calc(100vw - ${XS_SENT_OFFSET}px), 18rem)`;         // portrait → chunked (narrow, like Full mode)
            } else if (isSmallScreen) {
                // On small screens, adjust based on video width
                calculatedSentenceWidth = dynamicVideoWidth <= 50 ? '200px' : '150px';
            } else if (isMediumScreen) {
                // On medium screens, more responsive to video width
                calculatedSentenceWidth = dynamicVideoWidth <= 50 ? '350px' :
                    dynamicVideoWidth <= 100 ? '300px' : '250px';
            } else {
                // On large screens, use specific mapping:
                // 30px video → 300px sentence
                // 90px video → 280px sentence  
                // 150px video → 250px sentence
                let sentenceWidth;
                if (dynamicVideoWidth <= 30) {
                    sentenceWidth = 300;
                } else if (dynamicVideoWidth <= 90) {
                    // Linear interpolation between 30px→300px and 90px→280px
                    const ratio = (dynamicVideoWidth - 30) / (90 - 30);
                    sentenceWidth = 300 + (280 - 300) * ratio;
                } else {
                    // Linear interpolation between 90px→280px and 150px→250px
                    const ratio = (dynamicVideoWidth - 90) / (150 - 90);
                    sentenceWidth = 280 + (250 - 280) * ratio;
                }
                calculatedSentenceWidth = `${Math.round(sentenceWidth)}px`;
            }

            return {
                ...baseStyles,
                width: calculatedSentenceWidth,
                maxWidth: calculatedSentenceWidth,
                // xs minWidth must match the calc width — table auto-layout would otherwise
                // shrink the transcript to fit and there'd be nothing to swipe.
                minWidth: isXSmallScreen ? (isLandscape ? calculatedSentenceWidth : '0px') : isSmallScreen ? '150px' : '250px',
                flex: isXSmallScreen || isSmallScreen ? 1 : 'none',
                padding: getResponsiveValue(
                    isXSmallScreen,
                    isSmallScreen,
                    isMediumScreen,
                    isLargeScreen,
                    {
                        xs: '2px 2px 2px 12px',
                        sm: '2px 2px 2px 15px',
                        md: '3px 3px 3px 18px',
                        default: '4px 4px 4px 20px'
                    },
                    '4px 4px 4px 20px'
                )
            };
        }

        // Adjust video cell width on different screens
        if (obj.type === 'video') {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            // Apply responsive constraints while respecting user's video width setting
            const getConstrainedWidth = (screenSize) => {
                let maxAllowed, minAllowed;

                if (isXSmallScreen) {
                    minAllowed = 55; maxAllowed = 55; // Fixed 55px on phones — keeps sticky offsets predictable
                } else if (isSmallScreen) {
                    minAllowed = 20; maxAllowed = 250; // Tablet range  
                } else {
                    minAllowed = 30; maxAllowed = 400; // Desktop range
                }

                // Constrain user's preference within screen-appropriate limits
                const constrainedWidth = Math.max(minAllowed, Math.min(dynamicVideoWidth, maxAllowed));
                return `${constrainedWidth}px`;
            };

            const responsiveWidth = getConstrainedWidth();

            return {
                ...baseStyles,
                width: responsiveWidth,
                maxWidth: responsiveWidth,
                minWidth: responsiveWidth,
                padding: getResponsiveValue(
                    isXSmallScreen,
                    isSmallScreen,
                    isMediumScreen,
                    isLargeScreen,
                    {
                        xs: '2px 8px 2px 2px',    // Extra spacing on mobile
                        sm: '2px 10px 2px 2px',   // More spacing on small screens
                        md: '2px 12px 2px 2px',   // Even more spacing on medium screens
                        default: '2px 15px 2px 2px' // Maximum spacing on large screens
                    },
                    '2px 15px 2px 2px'
                ),
                // Prevent the column from expanding beyond the set width
                boxSizing: 'border-box',
                overflow: 'hidden',
            };
        }

        return baseStyles;
    };


    // Double-click anywhere in the row — including empty space in any cell — picks the
    // clip. Previously only the transcript cell did, which is invisible to a newcomer.
    const rowIsSelected = filterStore.isSelectedSentence(data['id'], currentView);

    // Every clickable data point gets the same dotted underline, so once you learn one
    // you know they all work.
    const clickableStyle = {
        cursor: 'pointer',
        borderBottom: '1px dotted rgba(224,70,107,0.45)',
    };

    // Traditional-transcript reading: name the speaker only where they change, above their
    // first line, instead of stamping it on every row. Lives inside the transcript cell — a
    // real heading row would mean injecting a <tr>, which this component cannot do.
    const speakerChanged = speakerHeadings && data['speaker'] && data['speaker'] !== prevSpeaker;
    const rawSpeakerName = speakerChanged
        ? ((speakerNames && speakerNames[data['speaker']]) || data['speaker'])
        : null;
    const speakerName = rawSpeakerName
        ? (/^[A-Z]$/i.test(String(rawSpeakerName).trim()) ? 'Speaker ' + String(rawSpeakerName).trim() : rawSpeakerName)
        : null;

    // The hint waits for you to STOP moving, shows where you came to rest, then fades.
    // A native title attribute trailed the cursor around, which was maddening.
    const [dblHint, setDblHint] = useState(null);      // { x, y } once settled
    const restTimer = useRef(null);
    const fadeTimer = useRef(null);
    const clearHintTimers = () => {
        if (restTimer.current) { clearTimeout(restTimer.current); restTimer.current = null; }
        if (fadeTimer.current) { clearTimeout(fadeTimer.current); fadeTimer.current = null; }
    };
    useEffect(() => clearHintTimers, []);

    const dblSelectProps = penMode ? {} : {
        onDoubleClick: (e) => {
            // leave anything with its own double-click behaviour alone
            if (e.target.closest('video, input, button, a, textarea')) return;
            clearHintTimers(); setDblHint(null);
            onCheckboxChange({ target: { checked: !rowIsSelected }, shiftKey: false }, item, data['id']);
        },
        onMouseMove: (e) => {
            if (e.target.closest('video, input, button, a, textarea, [data-word-index]')) return;
            const x = e.clientX, y = e.clientY;
            clearHintTimers();
            if (dblHint) setDblHint(null);            // moving again? hide until you settle
            restTimer.current = setTimeout(() => {     // settled for a beat → show it
                setDblHint({ x, y });
                fadeTimer.current = setTimeout(() => setDblHint(null), 2200);
            }, 550);
        },
        onMouseLeave: () => { clearHintTimers(); setDblHint(null); },
    };

    // MUST be portalled. This component returns <td>s straight into a <tr>, so rendering a
    // <div> alongside them is invalid HTML — the browser foster-parents it out of the table
    // and the row's cells visibly shift sideways. Send it to <body> instead.
    const dblHintEl = dblHint ? createPortal((
        <Box sx={{
            position: 'fixed', left: dblHint.x + 14, top: dblHint.y + 16, zIndex: 12000,
            pointerEvents: 'none', px: '9px', py: '5px', borderRadius: '6px', whiteSpace: 'nowrap',
            bgcolor: '#0f0d14', border: '1px solid #453d52', color: '#f2eef6',
            fontSize: '11.5px', fontWeight: 700, boxShadow: '0 6px 18px rgba(0,0,0,0.6)',
            animation: 'ccHintIn .18s ease',
            '@keyframes ccHintIn': { from: { opacity: 0, transform: 'translateY(3px)' }, to: { opacity: 1, transform: 'none' } },
        }}>
            {rowIsSelected ? 'Double-click to unselect' : 'Double-click to select'}
        </Box>
    ), document.body) : null;

    return (
        <Fragment>
            {dblHintEl}
            {header.map((obj, index) => {
                // Only render visible columns and apply responsive filtering
                if (obj.visible && shouldShowColumn(obj.field)) {
                    if (obj.type === 'checkbox') {
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} checkbox`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, ...getStickyStyle(obj), ...(data['source'] ? { boxShadow: `inset 4px 0 0 ${data['source']['color']}` } : {}) }}
                                // Phone: the whole (row-tall) cell is the tap target — a 14px
                                // checkbox alone is too small for thumbs. The checkbox's own
                                // onClick stopPropagations so it doesn't double-toggle.
                                onClick={isXSmallScreen ? (event) => {
                                    const nowChecked = !filterStore.isSelectedSentence(data['id'], currentView);
                                    onCheckboxChange({ target: { checked: nowChecked }, shiftKey: event.shiftKey }, item, data['id']);
                                } : undefined}
                            >
                                <Stack direction={isXSmallScreen ? "column" : "row"} spacing={0} alignItems={"center"} justifyContent={isXSmallScreen ? "center" : "left"}>
                                    {!isXSmallScreen && <Typography style={{ marginLeft: 2, fontSize: 11 }}>{item + 1}</Typography>}
                                    {/* ARCHIVE: which video this clip is from (V1, V2 …); hover for the title. */}
                                    {data['source'] && (
                                        <span title={data['source']['label']} style={{ fontSize: isXSmallScreen ? 8 : 9, fontWeight: 800, color: data['source']['color'], marginLeft: isXSmallScreen ? 0 : 3, letterSpacing: 0.3, lineHeight: 1 }}>
                                            {data['source']['short']}
                                        </span>
                                    )}
                                    <Checkbox
                                        sx={{
                                            '& .MuiSvgIcon-root': { fontSize: isSmallScreen ? 14 : 15 },
                                            '&.Mui-checked': { color: '#E53935' },
                                            padding: isXSmallScreen ? '4px 2px' : '3px', // compact so it fits the column and never clips
                                        }}
                                        value={data['id']}
                                        checked={filterStore.isSelectedSentence(data['id'], currentView)}
                                        onChange={() => {}}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            const nowChecked = !filterStore.isSelectedSentence(data['id'], currentView);
                                            onCheckboxChange({ target: { checked: nowChecked }, shiftKey: event.shiftKey }, item, data['id']);
                                        }}
                                    />
                                    {/* Phone: pick order (1st/2nd/3rd…) right under the box —
                                        the 'order' column is hidden on xs, so this is where
                                        the stitch order lives on mobile. */}
                                    {isXSmallScreen && selectionOrder !== null && (
                                        <span style={{ fontSize: '9px', fontWeight: 700, color: '#E53935', lineHeight: 1, marginTop: '-2px' }}>
                                            {getOrdinal(selectionOrder)}
                                        </span>
                                    )}
                                </Stack>

                            </td>
                        )
                    }
                    if (obj.type === 'listCategory') {
                        // Check if the field exists and is an array before accessing length
                        if (data[obj.field] && Array.isArray(data[obj.field]) && data[obj.field].length > 0) {
                            return (
                                <td {...dblSelectProps} key={index} className={`${obj.field} listCategory`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                    {data[obj.field].map((item) => {
                                        return <p key={item['id']} style={{ margin: '2px 0' }}>{item['category']}</p>
                                    })}
                                </td>
                            )
                        } else {
                            return (
                                <td {...dblSelectProps} key={index} className={`${obj.field} listCategory`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                </td>
                            )
                        }
                    }
                    if (obj.type === 'video') {
                        // Audio uploads have no thumbnail — show the CherryClipz play logo instead
                        // of a collapsed/black box so users know the clip is there and clickable.
                        // A poster generated in the BACKGROUND (after the ET opened) arrives via the
                        // clipThumbnails map keyed by clip id — prefer it, then the streamed value.
                        const posterUrl = (clipThumbnails && data && clipThumbnails[data['id']]) || (data && data['thumbnail']) || '';
                        const hasThumb = !!posterUrl;
                        // Phone: emotion + speaker live as a tiny chip on the thumbnail (their
                        // columns are laid out before 'play' in headerConstants, so showing them
                        // as columns on xs would land LEFT of the thumbnail and break the sticky
                        // offsets — the overlay keeps the data without the layout cost).
                        const xsSentColor = { positive: '#4ade80', neutral: '#cbd5e1', negative: '#f87171' }[data['sentiment']];
                        const xsSentSymbol = { positive: '+', neutral: '=', negative: '−' }[data['sentiment']];
                        const videoEl = (
                            <video id={`v-${data['id']}`}
                                playsInline
                                className="videos"
                                style={
                                    isXSmallScreen
                                        // Phone: a fixed, solid landscape thumbnail box. A FIXED pixel
                                        // width is what stops the column from collapsing to a thin sliver
                                        // (the table is tableLayout:auto, so a width:100% video can shrink to
                                        // nothing under layout pressure; a fixed width sets a hard minimum).
                                        // objectFit:cover fills the box so every clip is clearly visible/tappable.
                                        ? {
                                            width: `${XS_THUMB_W}px`,
                                            height: `${XS_THUMB_H}px`, // keeps the 142:80 landscape ratio
                                            objectFit: 'cover',
                                            display: 'block',
                                            borderRadius: '4px',
                                            ...(hasThumb ? {} : { background: `#15151c url(${logoSite}) center / 40px auto no-repeat` }),
                                        }
                                        : (hasThumb
                                            ? {
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                            }
                                            // audio: fixed box with the CherryClipz play logo centered as background
                                            : {
                                                width: '100%',
                                                height: '110px',
                                                display: 'block',
                                                borderRadius: '4px',
                                                background: `#15151c url(${logoSite}) center / 54px auto no-repeat`,
                                            })
                                }
                                src={(data['source'] && data['source']['video']) || videoFile} type="video/mp4"
                                preload="none"
                                poster={posterUrl}
                                onClick={(event) => {
                                    onVideoClick(event, data, item)
                                }}
                            />
                        );
                        // This clip's poster is still being generated in the background —
                        // show a small badge so the logo placeholder reads as "loading".
                        const generatingBadge = (!hasThumb && thumbnailsGenerating) ? (
                            <span style={{
                                position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)',
                                fontSize: 9, fontWeight: 600, letterSpacing: 0.3, color: '#fff',
                                background: 'rgba(233,30,79,0.9)', padding: '1px 7px', borderRadius: 999,
                                pointerEvents: 'none', whiteSpace: 'nowrap',
                            }}>creating…</span>
                        ) : null;
                        // Phone: emotion + speaker as a chip on the thumbnail. Enlarged when the
                        // "Big text" extra is on; reverts to the original small chip when off.
                        const bigChip = etExtras.bigLetters;
                        const spkEmoOverlay = (isXSmallScreen && (xsSentSymbol || data['speaker'])) ? (
                            <span style={{
                                position: 'absolute', left: '6px', bottom: bigChip ? '6px' : '10px', zIndex: 2, pointerEvents: 'none',
                                display: 'inline-flex', alignItems: 'center', gap: bigChip ? '4px' : '3px',
                                fontSize: bigChip ? '13px' : '9px', fontWeight: bigChip ? 800 : 700, lineHeight: 1,
                                background: bigChip ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.55)', borderRadius: bigChip ? '5px' : '4px', padding: bigChip ? '3px 6px' : '2px 4px',
                            }}>
                                {xsSentSymbol && <span style={{ color: xsSentColor, fontSize: bigChip ? '15px' : '10px' }}>{xsSentSymbol}</span>}
                                {data['speaker'] && <span style={{ color: '#e6e8f0' }}>{String(data['speaker']).slice(0, 8)}</span>}
                            </span>
                        ) : null;
                        return (
                            // position:'relative' would OVERRIDE position:'sticky' on xs (this was
                            // the bug that let thumbnails drift over the transcript) — sticky is
                            // itself a positioned box, so absolute children still anchor to it.
                            <td {...dblSelectProps} key={index} className={`${obj.field} video`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, ...getStickyStyle(obj), ...(isXSmallScreen ? {} : { position: 'relative' }) }}>
                                {isXSmallScreen ? (
                                    // Phone: thumbnail (with the emo/speaker chip anchored to IT), then the
                                    // clip's in/out timecode and a Download button beneath — so you can read
                                    // timing and grab one clip without hunting for the timecode column.
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ position: 'relative', width: `${XS_THUMB_W}px`, height: `${XS_THUMB_H}px`, flexShrink: 0 }}>
                                            {videoEl}
                                            {generatingBadge}
                                            {spkEmoOverlay}
                                        </div>
                                        {etExtras.timecode && Array.isArray(displayDuration) && (
                                            // The SAME Clock as the timecode column — so the in/out points
                                            // have the same − / + adjust steppers here under the thumbnail.
                                            <Clock
                                                header={true}
                                                squareButtons={labs.tidyTimes}
                                                                                                duration={precision ? displayDuration : displayDuration.map((t) => String(t).split('.')[0])}
                                                item={data}
                                                onAdjustTimes={onAdjustTimes}
                                                adjustedBy={data._adjustedBy || (penAdjusted ? 'user' : null)}
                                                showJump={false}
                                            />
                                        )}
                                        {etExtras.download && onSingleClipDownload && !IS_PANEL_EMBED && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onSingleClipDownload(data); }}
                                                style={onSingleClipShare
                                                    ? { ...XS_BTN, background: '#1b1a20', border: '1px solid rgba(255,255,255,0.2)' }
                                                    : {
                                                        display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                                                        background: '#1b1a20', border: '1px solid rgba(255,255,255,0.2)', color: '#fff',
                                                        borderRadius: '8px', padding: '5px 12px', fontSize: '12px', fontWeight: 700,
                                                        fontFamily: 'Outfit, sans-serif',
                                                      }}
                                            >
                                                <FileDownloadIcon sx={{ fontSize: 15 }} /> Download
                                            </button>
                                        )}
                                        {/* Stacked, not side by side: this column is ~150px wide, so two
                                            pills in a row would each be ~70px and the labels would wrap. */}
                                        {onSingleClipShare && !IS_PANEL_EMBED && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onSingleClipShare(data); }}
                                                style={{ ...XS_BTN, background: 'rgba(230,29,80,0.18)', border: '1px solid #E61D50' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                     strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                                    <path d="M12 3v12" /><path d="M8.5 6.5 12 3l3.5 3.5" />
                                                    <path d="M7.5 10H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.5" />
                                                </svg> Share
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {videoEl}
                                        {generatingBadge}
                                    </>
                                )}
                            </td>
                        )
                    }

                    if (obj.type === 'boolean') {
                        const boolSymbol = obj.field === 'contains_numbers'
                            ? <span style={{ fontSize: '15px', fontWeight: 700, color: '#546e7a', lineHeight: 1 }}>#</span>
                            : <span style={{ fontSize: '18px', fontWeight: 700, color: '#ba68c8', lineHeight: 1 }}>?</span>;
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} boolean`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, textAlign: 'center' }}>
                                {data[obj.field] ? (
                                    onBoolClick ? (
                                        <span onClick={(e) => { e.stopPropagation(); onBoolClick(obj.field); }}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            title={obj.field === 'contains_numbers' ? 'Show only clips with a number — click again for all' : 'Show only questions — click again for all'}
                                            style={{ ...clickableStyle, padding: '0 3px' }}>
                                            {boolSymbol}
                                        </span>
                                    ) : boolSymbol
                                ) : null}
                            </td>
                        )
                    }
                    if (obj.field === 'sentence') {
                        const editedText = editedSentences?.[data['id']];
                        const displaySentence = editedText ?? data[obj.field];
                        // Words removed by editing (shown as grey strikethrough)
                        const removedWords = editedText
                            ? (() => {
                                const editSet = new Set(editedText.toLowerCase().split(/\s+/));
                                return (data[obj.field] || '').split(/\s+/).filter(w => w && !editSet.has(w.toLowerCase()));
                              })()
                            : [];

                        let search;
                        if (filterStore.getSearch(currentView).includes(' ')) {
                            search = filterStore.getSearch(currentView).split(' ').filter(word => word !== '');
                        } else {
                            search = [filterStore.getSearch(currentView)];
                        }
                        const words = displaySentence.split(' ');

                        // Get pen highlight range for this clip
                        const penHighlight = filterStore.getHighlight(data['id']);

                        // Build spans with data-word-index and pen-highlight class where applicable
                        let html = words.map((word, index) => {
                            const isPenHighlighted = penHighlight && index >= penHighlight.start && index <= penHighlight.end;
                            const wordTime = data.utterance && data.utterance[index] ? ` data-word-time="${data.utterance[index][0]}"` : '';
                            return `<span id="s-${data['id']}-${index}" data-word-index="${index}"${wordTime}${isPenHighlighted ? ' class="pen-highlight"' : ''}>${word}</span>`;
                        });

                        // Restore search highlighting functionality
                        for (let i = 0; i < words.length; i++) {
                            for (let j = 0; j < search.length; j++) {
                                const word = words[i];
                                const wordSearch = words[i].toLowerCase();
                                const isPenHighlighted = penHighlight && i >= penHighlight.start && i <= penHighlight.end;
                                const penClass = isPenHighlighted ? ' pen-highlight' : '';
                                // exact match
                                if (wordSearch === search[j]) {
                                    html[i] = `<span id="s-${data['id']}-${i}" data-word-index="${i}" class="highlight${penClass}">${word}</span>`;
                                }
                                if (search[j] !== '') {
                                    // starts with
                                    if (wordSearch.startsWith(search[j])) {
                                        html[i] = `<span id="s-${data['id']}-${i}" data-word-index="${i}" class="highlight${penClass}">${word.slice(0, search[j].length)}</span>${word.slice(search[j].length)}`;
                                    }
                                }
                            }
                        }

                        // Insert a "…" pause marker wherever there's a long silent gap
                        // between two spoken words (e.g. "Oh … Fudge"). Done as a final
                        // pass so it survives the search-highlight rebuild above. The
                        // marker has no word index/time, so it's ignored by click-to-seek
                        // and pen selection. Hover shows the pause length.
                        const PAUSE_THRESHOLD = 1.0; // seconds
                        const utt = data.utterance;
                        const pieces = [];
                        for (let i = 0; i < html.length; i++) {
                            pieces.push(html[i]);
                            if (utt && utt[i] && utt[i + 1] && Array.isArray(utt[i]) && Array.isArray(utt[i + 1])) {
                                const gap = utt[i + 1][0] - utt[i][1];
                                if (gap >= PAUSE_THRESHOLD) {
                                    pieces.push(`<span class="pause-gap" style="opacity:0.5;font-weight:700;letter-spacing:1px;" title="${gap.toFixed(1)}s pause">…</span>`);
                                }
                            }
                        }
                        const htmlJoined = pieces.join(' ');

                        const isSelected = filterStore.isSelectedSentence(data['id'], currentView);
                        return (
                            <td {...dblSelectProps}
                                key={index}
                                className={`${obj.field} ${obj.type}`}
                                style={{ ...getResponsiveStyles(obj), ...getStickyStyle(obj), ...rowHighlight, cursor: penMode ? 'crosshair' : undefined }}
                                onClick={(isXSmallScreen && !IS_PANEL_EMBED) ? () => onCheckboxChange({ target: { checked: !isSelected } }, item, data['id']) : (!penMode && onWordClick ? (e) => {
                                    const span = e.target.closest('[data-word-time]');
                                    if (span) { const t = parseFloat(span.dataset.wordTime); const wi = parseInt(span.dataset.wordIndex); if (!isNaN(t)) onWordClick(data, item, t, wi); }
                                } : undefined)}
                                onDoubleClick={!penMode ? () => onCheckboxChange({ target: { checked: !isSelected }, shiftKey: false }, item, data['id']) : undefined}
                                onMouseDown={penMode ? (e) => {
                                    const span = e.target.closest('[data-word-index]');
                                    if (!span) return;
                                    dragStartRef.current = parseInt(span.dataset.wordIndex);
                                    e.preventDefault();
                                } : undefined}
                                onMouseMove={penMode ? (e) => {
                                    if (dragStartRef.current === null) return;
                                    const span = e.target.closest('[data-word-index]');
                                    if (!span) return;
                                    const hoverIdx = parseInt(span.dataset.wordIndex);
                                    const lo = Math.min(dragStartRef.current, hoverIdx);
                                    const hi = Math.max(dragStartRef.current, hoverIdx);
                                    e.currentTarget.querySelectorAll('[data-word-index]').forEach(s => {
                                        const idx = parseInt(s.dataset.wordIndex);
                                        s.classList.toggle('pen-preview', idx >= lo && idx <= hi);
                                    });
                                } : undefined}
                                onMouseUp={penMode ? (e) => {
                                    if (dragStartRef.current === null) return;
                                    e.currentTarget.querySelectorAll('[data-word-index]').forEach(s => s.classList.remove('pen-preview'));
                                    const span = e.target.closest('[data-word-index]');
                                    if (!span) { dragStartRef.current = null; return; }
                                    const endWord = parseInt(span.dataset.wordIndex);
                                    const startWord = dragStartRef.current;
                                    dragStartRef.current = null;
                                    const currentHighlight = filterStore.getHighlight(data['id']);
                                    if (currentHighlight && startWord === endWord &&
                                        startWord >= currentHighlight.start && startWord <= currentHighlight.end) {
                                        filterStore.clearHighlight(data['id']);
                                    } else {
                                        filterStore.setHighlight(data['id'], startWord, endWord);
                                    }
                                } : undefined}
                                onContextMenu={!penMode && onWordRightClick ? (e) => {
                                    const span = e.target.closest('[data-word-index]');
                                    if (span) { e.preventDefault(); onWordRightClick(data, parseInt(span.dataset.wordIndex)); }
                                } : undefined}
                                onMouseLeave={penMode ? (e) => {
                                    if (dragStartRef.current === null) return;
                                    e.currentTarget.querySelectorAll('[data-word-index]').forEach(s => s.classList.remove('pen-preview'));
                                    dragStartRef.current = null;
                                } : undefined}
                            >
                                <div style={{
                                    fontSize: (() => {
                                        const base = transcriptStyle?.size || (isXSmallScreen ? 11 : isSmallScreen ? 14 : 16);
                                        // Keepers grow a touch (and filler shrinks a touch) so the gold reads bigger.
                                        const adj = isFillerKeep ? 2 : (isFillerDim ? -1 : 0);
                                        return `${base + adj}px`;
                                    })(),
                                    fontWeight: transcriptStyle?.bold ? 700 : (isFillerKeep ? 600 : undefined),
                                    fontStyle: transcriptStyle?.italic ? 'italic' : undefined,
                                    lineHeight: isXSmallScreen ? '1.2' : '1.4',
                                    fontFamily: transcriptStyle?.fontCSS || undefined,
                                }}>
                                    {speakerName && (
                                        <span
                                            onClick={onSpeakerClick ? (e) => { e.stopPropagation(); onSpeakerClick(data['speaker']); } : undefined}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            title={onSpeakerClick ? `Show only ${speakerName} — click again for everyone` : undefined}
                                            style={{
                                            cursor: onSpeakerClick ? 'pointer' : 'default',
                                            display: 'block', marginBottom: '9px',
                                            fontSize: '0.95em', fontWeight: 800, letterSpacing: '.06em',
                                            textTransform: 'uppercase', color: '#e0466b',
                                            borderBottom: '2px solid #e0466b', paddingBottom: '4px',
                                            width: 'fit-content',
                                        }}>{speakerName}</span>
                                    )}
                                    {data._clippedStart && <span style={{ color: '#ef5350', fontWeight: 700 }} title="Text cut — clip start was pushed forward">...</span>}
                                    {data._stolenTextBefore && (() => {
                                        const hl = filterStore.getHighlight(data['id']);
                                        const inc = !!(hl && hl.incBefore);
                                        const lastW = words ? Math.max(0, words.length - 1) : 0;
                                        const onStolenClick = penMode
                                            ? (e) => { e.stopPropagation(); if (!filterStore.getHighlight(data['id'])) filterStore.setHighlight(data['id'], 0, lastW); filterStore.toggleHighlightStolen(data['id'], 'before'); }
                                            : (onWordClick && data._stolenTimeBefore != null ? (e) => { e.stopPropagation(); onWordClick(data, item, data._stolenTimeBefore, -1); } : undefined);
                                        return (
                                            <span
                                                className={inc ? 'pen-highlight' : undefined}
                                                style={{ color: inc ? undefined : '#ef5350', cursor: (penMode || (onWordClick && data._stolenTimeBefore != null)) ? 'pointer' : 'default', borderRadius: '3px' }}
                                                title={penMode ? (inc ? 'Borrowed words included — click to drop them' : 'Click to pull these borrowed words into your clip') : (inc ? 'Borrowed words included in this clip' : (onWordClick && data._stolenTimeBefore != null ? `Seek to ${data._stolenTimeBefore.toFixed(2)}s (stolen from previous clip)` : 'Stolen from previous clip'))}
                                                onClick={onStolenClick}
                                            > {data._stolenTextBefore} </span>
                                        );
                                    })()}
                                    {parse(htmlJoined)}
                                    {data._stolenTextAfter && (() => {
                                        const hl = filterStore.getHighlight(data['id']);
                                        const inc = !!(hl && hl.incAfter);
                                        const lastW = words ? Math.max(0, words.length - 1) : 0;
                                        const onStolenClick = penMode
                                            ? (e) => { e.stopPropagation(); if (!filterStore.getHighlight(data['id'])) filterStore.setHighlight(data['id'], 0, lastW); filterStore.toggleHighlightStolen(data['id'], 'after'); }
                                            : (onWordClick && data._stolenTimeAfter != null ? (e) => { e.stopPropagation(); onWordClick(data, item, data._stolenTimeAfter, -1); } : undefined);
                                        return (
                                            <span
                                                className={inc ? 'pen-highlight' : undefined}
                                                style={{ color: inc ? undefined : '#ef5350', cursor: (penMode || (onWordClick && data._stolenTimeAfter != null)) ? 'pointer' : 'default', borderRadius: '3px' }}
                                                title={penMode ? (inc ? 'Borrowed words included — click to drop them' : 'Click to pull these borrowed words into your clip') : (inc ? 'Borrowed words included in this clip' : (onWordClick && data._stolenTimeAfter != null ? `Seek to ${data._stolenTimeAfter.toFixed(2)}s (stolen from next clip)` : 'Stolen from next clip'))}
                                                onClick={onStolenClick}
                                            >{data._stolenTextAfter}</span>
                                        );
                                    })()}
                                    {data._clippedEnd && <span style={{ color: '#ef5350', fontWeight: 700 }} title="Text cut — clip end was pushed back"> ...</span>}
                                </div>
                                {penHighlight && !penMode && (
                                    <span style={{ fontSize: '10px', color: '#F59E0B', marginLeft: '4px', opacity: 0.7 }} title="This clip has a highlight — enable pen mode to edit">&#9999;</span>
                                )}
                                {splitCursor && splitCursor.rowId === data['id'] && (
                                    <div style={{
                                        marginTop: '6px',
                                        padding: '6px 10px',
                                        background: '#2d2200',
                                        border: '1px solid #f59e0b88',
                                        borderRadius: '6px',
                                        userSelect: 'none',
                                    }}>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b', marginBottom: '2px' }}>
                                            ✂ Split before &ldquo;{((data['sentence'] || '').split(' '))[splitCursor.wordIndex] || ''}&rdquo;
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#d4a017', lineHeight: 1.4 }}>
                                            This clip will become <strong>2 shorter clips</strong> — one ending before this word, one starting here.
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#a0a0a0', marginTop: '3px' }}>
                                            Press <strong style={{ color: '#f59e0b' }}>Enter</strong> to split &nbsp;·&nbsp; <strong style={{ color: '#f59e0b' }}>Esc</strong> to cancel
                                        </div>
                                    </div>
                                )}
                                {data['_splitId'] && onUnsplit && (
                                    <div
                                        style={{ fontSize: '11px', color: '#90a4ae', marginTop: '3px', cursor: 'pointer', userSelect: 'none', display: 'inline-block' }}
                                        onClick={(e) => { e.stopPropagation(); onUnsplit(data['_splitId']); }}
                                        title="Merge split clips back together"
                                    >
                                        ↩ Unsplit
                                    </div>
                                )}
                                {/* Removed words (greyed strikethrough when sentence was edited) */}
                                {removedWords.length > 0 && (
                                    <div style={{ marginTop: '3px', fontSize: '11px', color: '#888', lineHeight: 1.4 }}>
                                        {removedWords.map((w, i) => (
                                            <span key={i} style={{ textDecoration: 'line-through', marginRight: '4px', opacity: 0.7 }}>{w}</span>
                                        ))}
                                    </div>
                                )}
                                {translations && translations[data['id']] && (
                                    <div style={{
                                        marginTop: '5px',
                                        fontSize: transcriptStyle?.size ? `${Math.max(11, transcriptStyle.size - 2)}px` : (isXSmallScreen ? '11px' : '13px'),
                                        fontFamily: transcriptStyle?.fontCSS || undefined,
                                        color: '#90caf9',
                                        fontStyle: 'italic',
                                        lineHeight: 1.4,
                                        borderLeft: '2px solid #0288d1',
                                        paddingLeft: '6px',
                                    }}>
                                        {translations[data['id']]}
                                    </div>
                                )}
                                {/* Edit button — amber if edited, subtle grey otherwise */}
                                {onEditSentence && (
                                    <Tooltip title={editedSentences?.[data['id']] ? 'Sentence edited — click to re-edit or restore original' : 'Edit this transcript sentence'} placement="top">
                                        <IconButton size="small"
                                            onClick={(e) => { e.stopPropagation(); onEditSentence(data); }}
                                            sx={{ padding: '2px', opacity: editedSentences?.[data['id']] ? 1 : 0.3,
                                                '&:hover': { opacity: 1 },
                                                display: 'inline-flex', verticalAlign: 'middle', ml: '4px' }}>
                                            <EditIcon sx={{ fontSize: '12px', color: editedSentences?.[data['id']] ? '#f59e0b' : 'inherit' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </td>
                        )
                    }
                    if (obj.field === 'sec') {
                        const secColor = data._adjustedBy === 'user' ? '#26c6da' : data._adjustedBy === 'cascade' ? '#ffb74d' : undefined;
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, paddingLeft: isXSmallScreen ? '8px' : '20px', color: secColor, fontWeight: secColor ? 600 : undefined }}>
                                {onSecondsClick ? (
                                    <span onClick={(e) => { e.stopPropagation(); onSecondsClick(data['sec_int'] ?? data[obj.field]); }}
                                        onDoubleClick={(e) => e.stopPropagation()}
                                        title="Show only clips this long — click again for all"
                                        style={clickableStyle}>
                                        {precision ? data[obj.field] : data['sec_int']}
                                    </span>
                                ) : (precision ? data[obj.field] : data['sec_int'])}
                            </td>
                        )
                    }
                    if (obj.field === 'virality_score') {
                        const v = data[obj.field];
                        const has = v != null && v !== '';
                        // red (low) → amber (mid) → green (high), so the eye finds the bangers fast
                        const score = has ? Math.max(0, Math.min(100, Number(v))) : 0;
                        const hue = Math.round((score / 100) * 120); // 0=red, 120=green
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, textAlign: 'center' }}>
                                {has ? (
                                    <span style={{
                                        display: 'inline-block', minWidth: '26px', padding: '2px 6px', borderRadius: '6px',
                                        fontSize: '13px', fontWeight: 700, lineHeight: 1.1,
                                        color: `hsl(${hue}, 75%, 78%)`, backgroundColor: `hsla(${hue}, 70%, 40%, 0.22)`,
                                    }}>{score}</span>
                                ) : null}
                            </td>
                        )
                    }
                    if (obj.field === 'order') {
                        // Reserve a fixed-width slot: selecting a clip swaps the plain row number for a
                        // BOLD 15px "1st/2nd" pick badge, which is wider (dramatically so on short videos
                        // where row numbers are 1–2 digits). The ET table is tableLayout:auto, so that
                        // wider content would grow the whole 'order' column and shove every row to the
                        // right ("ET jumps right on select"). Pinning the cell + an inner fixed-width box
                        // keeps the content width constant in BOTH states, so the layout never moves.
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, width: '40px', minWidth: '40px', maxWidth: '40px' }}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    <span style={{ display: 'inline-block', width: '40px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                        {data[obj.field] === maxLines ? '' : (
                                            selectionOrder !== null
                                                ? <span style={{ fontSize: '15px', fontWeight: 700, color: '#E53935' }}>{getOrdinal(selectionOrder)}</span>
                                                : data[obj.field]
                                        )}
                                    </span>
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'sentiment') {
                        // Map sentiment values to compact symbols
                        const sentimentSymbols = {
                            'positive': '+',
                            'neutral': '=',
                            'negative': '-'
                        };

                        // Map sentiment values to tooltip text
                        const sentimentTooltips = {
                            'positive': 'Positive sentiment',
                            'neutral': 'Neutral sentiment',
                            'negative': 'Negative sentiment'
                        };

                        return (
                            <td {...dblSelectProps} key={index} className={`${data[obj.field]} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    <Tooltip
                                        title={sentimentTooltips[data[obj.field]] || data[obj.field]}
                                        placement="top"
                                        arrow
                                    >
                                        <Typography
                                            variant="body1"
                                            onClick={onSentimentClick && data[obj.field] ? (e) => { e.stopPropagation(); onSentimentClick(data[obj.field]); } : undefined}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            sx={{
                                                fontSize: isXSmallScreen ? '14px' : '32px',
                                                fontWeight: 'bold',
                                                lineHeight: 1,
                                                cursor: onSentimentClick && data[obj.field] ? 'pointer' : 'default'
                                            }}
                                        >
                                            {sentimentSymbols[data[obj.field]] || data[obj.field]}
                                        </Typography>
                                    </Tooltip>
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'speaker') {
                        const spkLetter = data[obj.field];
                        const spkNamed = spkLetter && speakerNames ? speakerNames[spkLetter] : '';
                        // ARCHIVE rows: the speaker value is already the label ("A·2" or a linked name) — no prefix.
                        const spkLabel = spkNamed || (spkLetter ? (data['source'] ? String(spkLetter) : `Speaker ${spkLetter}`) : '');
                        const canRename = !!onRenameSpeaker && !!spkLetter;
                        const commitSpk = () => { if (onRenameSpeaker) onRenameSpeaker(spkLetter, spkDraft); setSpkEditing(false); };
                        return (
                            <td {...dblSelectProps} key={index} className={`${data[obj.field]} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                <Stack direction="row" spacing={0} alignItems={"center"} justifyContent="center">
                                    {spkEditing && canRename ? (
                                        <input
                                            autoFocus
                                            value={spkDraft}
                                            onChange={(e) => setSpkDraft(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); commitSpk(); }
                                                else if (e.key === 'Escape') { e.preventDefault(); setSpkEditing(false); }
                                            }}
                                            onBlur={commitSpk}
                                            placeholder={spkLetter ? `Speaker ${spkLetter}` : 'Name'}
                                            style={{ width: '92px', fontSize: isXSmallScreen ? '11px' : '13px', fontWeight: 600, color: '#fff', background: '#000', border: '1.5px solid #e5163f', borderRadius: '6px', padding: '3px 6px', outline: 'none' }}
                                        />
                                    ) : (
                                        <span
                                            onClick={canRename ? (e) => { e.stopPropagation(); setSpkDraft(spkNamed || ''); setSpkEditing(true); } : undefined}
                                            title={canRename ? (spkNamed ? `Rename (Speaker ${spkLetter})` : 'Click to name this speaker') : undefined}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: isXSmallScreen ? '12px' : '16px', whiteSpace: 'nowrap', fontWeight: 600, cursor: canRename ? 'text' : 'default', borderRadius: '5px', padding: canRename ? '2px 5px' : 0 }}
                                        >
                                            {spkNamed ? (
                                                <>
                                                    <span style={{ fontWeight: 700 }}>{spkNamed}</span>
                                                    <span style={{ fontSize: isXSmallScreen ? '9px' : '11px', fontWeight: 700, color: '#9a9aa2', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '4px', padding: '0 4px', lineHeight: '17px' }}>{spkLetter}</span>
                                                </>
                                            ) : spkLabel}
                                        </span>
                                    )}
                                </Stack>
                            </td>
                        )
                    }
                    if (obj.field === 'duration') {
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight, ...getStickyStyle(obj) }}>
                                <Clock
                                    header={true}
                                    squareButtons={labs.tidyTimes}
                                                                        duration={precision ? displayDuration : displayDuration.map(item => item.split('.')[0])}
                                    item={data}
                                    onJumpClick={onJumpToItem}
                                    onAdjustTimes={onAdjustTimes}
                                    adjustedBy={data._adjustedBy || (penAdjusted ? 'user' : null)}
                                    showJump={false}
                                />
                                {/* Context + download together, below the timecodes — so the
                                    in/out points recenter left (more room for the +/- buttons)
                                    and align with the LIST/PICK header. */}
                                {((showJump && onJumpToItem) || onSingleClipDownload) && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '2px' }}>
                                        {showJump && onJumpToItem && (
                                            <Tooltip title="Find this clip in context — jumps to it in the full transcript and clears search/filters/sort" placement="top" arrow enterDelay={150}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onJumpToItem(data); }}
                                                    sx={{ padding: '2px', color: 'primary.main', '&:hover': { backgroundColor: 'action.hover' } }}
                                                >
                                                    <CenterFocusStrongIcon sx={{ fontSize: isXSmallScreen ? 14 : 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {onSingleClipDownload && !IS_PANEL_EMBED && (
                                            <Tooltip title="Download this clip" placement="top" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onSingleClipDownload(data); }}
                                                    sx={onSingleClipShare
                                                        ? { ...ROW_ICON, color: '#b9b0c4', borderColor: 'rgba(255,255,255,0.22)',
                                                            '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.5)' } }
                                                        : { padding: '2px', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                >
                                                    <FileDownloadIcon sx={{ fontSize: isXSmallScreen ? 13 : 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {/* The desktop twin of the row's Share button. Without it the
                                            feature only existed in the phone layout, which is exactly
                                            where it wasn't being looked for. */}
                                        {onSingleClipShare && !IS_PANEL_EMBED && (
                                            <Tooltip title="Share this clip" placement="top" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onSingleClipShare(data); }}
                                                    sx={{ ...ROW_ICON, color: '#E61D50', borderColor: 'rgba(230,29,80,0.55)',
                                                          '&:hover': { color: '#fff', bgcolor: '#E61D50', borderColor: '#E61D50' } }}
                                                >
                                                    <svg width={isXSmallScreen ? 12 : 14} height={isXSmallScreen ? 12 : 14}
                                                         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                         strokeLinecap="round" strokeLinejoin="round"
                                                         style={{ display: 'block', transform: 'translateY(-0.5px)' }}>
                                                        <path d="M12 3v12" /><path d="M8.5 6.5 12 3l3.5 3.5" />
                                                        <path d="M7.5 10H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.5" />
                                                    </svg>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </td>
                        )
                    } else if (obj.field === 'entity_type' || obj.field === 'label') {

                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                {data[obj.field] !== undefined ? data[obj.field].charAt(0).toUpperCase() + data[obj.field].slice(1).replace(/_/g, ' ') : ''}
                            </td>
                        )
                    } else {
                        // For generic field types
                        const maxTextLength = getResponsiveValue(
                            isXSmallScreen,
                            isSmallScreen,
                            isMediumScreen,
                            isLargeScreen,
                            {
                                xs: 15,
                                sm: 20,
                                md: 30,
                                default: 100
                            },
                            100
                        );

                        // The word columns (Actions / Descriptions / Things) are the fastest
                        // route into "show me every clip about this" — so each word is a
                        // one-click search rather than dead text.
                        const WORD_COLS = ['verbs', 'adjectives', 'nouns', 'chachma', 'bina', 'daas',
                            'avraham', 'yitzchak', 'yaakov', 'torah', 'avoda', 'gimilat_hasidim',
                            'sound_effects', 'visuals_detected', 'camera_shot'];
                        const raw = data[obj.field];
                        if (obj.field === 'length' && onLengthClick && raw !== undefined && raw !== null && raw !== '') {
                            return (
                                <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                    <span onClick={(e) => { e.stopPropagation(); onLengthClick(raw); }}
                                        onDoubleClick={(e) => e.stopPropagation()}
                                        title={`Show only clips of ${raw} characters`}
                                        style={clickableStyle}>{raw}</span>
                                </td>
                            );
                        }
                        if (onWordSearch && WORD_COLS.indexOf(obj.field) !== -1 && typeof raw === 'string' && raw.trim()) {
                            const parts = raw.split(/[,\s]+/).filter(Boolean);
                            return (
                                <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                    {parts.map((w, wi) => (
                                        <span
                                            key={wi}
                                            onClick={(e) => { e.stopPropagation(); onWordSearch(w.replace(/[.,;:!?]+$/, '')); }}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            title={`Find every clip that says "${w}"`}
                                            style={{
                                                cursor: 'pointer', marginRight: '6px',
                                                borderBottom: '1px dotted rgba(224,70,107,0.45)',
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = '#e0466b'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
                                        >
                                            {w}
                                        </span>
                                    ))}
                                </td>
                            );
                        }
                        return (
                            <td {...dblSelectProps} key={index} className={`${obj.field} ${obj.type}`} style={{ ...getResponsiveStyles(obj), ...rowHighlight }}>
                                {(isSmallScreen || isXSmallScreen) &&
                                    typeof raw === 'string' &&
                                    raw.length > maxTextLength
                                    ? raw.substring(0, maxTextLength) + '...'
                                    : raw}
                            </td>
                        )
                    }
                }
                return null;
            })}
        </Fragment>
    );
});

